/**
 * /projects/:id — one project's workspace.
 *
 * Shows:
 *   - project header (name, robot, profile, postprocessor)
 *   - inputs: multi-STEP drop zone + table with per-file remove
 *   - "Generate all" button → blocking batch run → per-part result table
 *   - outputs: downloadable program.mod + metadata/trajectory/collision JSON
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Button,
  Callout,
  Card,
  HTMLTable,
  Icon,
  NonIdealState,
  Spinner,
  Tag,
} from '@blueprintjs/core'

import {
  deletePart,
  deleteProject,
  getProject,
  type ProjectDetail as ProjectDetailT,
  runProject,
  type ProjectRunResult,
  uploadParts,
} from '../api'
import { ProjectCellEditor } from '../components/ProjectCellEditor'
import { ProjectParamsEditor } from '../components/ProjectParamsEditor'

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const [proj, setProj] = useState<ProjectDetailT | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [running, setRunning] = useState(false)
  const [lastRun, setLastRun] = useState<ProjectRunResult | null>(null)

  const refresh = useCallback(async () => {
    if (!id) return
    try {
      setProj(await getProject(id))
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex))
    }
  }, [id])

  useEffect(() => { refresh() }, [refresh])

  const onDrop = async (files: FileList | null) => {
    if (!files || !id) return
    setUploading(true)
    try {
      await uploadParts(id, Array.from(files))
      await refresh()
    } finally {
      setUploading(false)
    }
  }

  const onRun = async () => {
    if (!id) return
    setRunning(true)
    try {
      const result = await runProject(id)
      setLastRun(result)
      await refresh()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex))
    } finally {
      setRunning(false)
    }
  }

  const onDelete = async () => {
    if (!id || !confirm(`Delete project "${proj?.name}" and all its files?`)) return
    await deleteProject(id)
    window.location.href = '/projects'
  }

  if (err) return <Callout intent="danger" icon="error">{err}</Callout>
  if (!proj) return <Spinner />

  return (
    <div className="projects-root">
      <header className="projects-header">
        <Link to="/projects" className="projects-back">← Projects</Link>
        <h2>{proj.name}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Tag minimal>{proj.robot}</Tag>
          <Tag minimal intent="primary">{proj.profile}</Tag>
          <Tag minimal>{proj.postprocessor}</Tag>
          <Button icon="trash" minimal intent="danger" onClick={onDelete}>Delete</Button>
        </div>
      </header>

      <ProjectCellEditor projectId={proj.project_id} />
      <ProjectParamsEditor projectId={proj.project_id} />

      <Card style={{ marginTop: 16 }}>
        <h3>Inputs</h3>
        <DropZone onDrop={onDrop} uploading={uploading} />
        {proj.inputs.length > 0 && (
          <HTMLTable compact striped style={{ marginTop: 10 }}>
            <thead>
              <tr><th>STEP file</th><th></th></tr>
            </thead>
            <tbody>
              {proj.inputs.map((f) => (
                <tr key={f}>
                  <td>{f}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button
                      icon="trash"
                      minimal
                      small
                      intent="danger"
                      onClick={async () => { await deletePart(id!, f); await refresh() }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </HTMLTable>
        )}
      </Card>

      <Card style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Run</h3>
          <Button
            icon="play"
            intent="primary"
            onClick={onRun}
            loading={running}
            disabled={proj.inputs.length === 0 || running}
          >
            Generate all ({proj.inputs.length})
          </Button>
        </div>
        {lastRun && (
          <HTMLTable compact striped style={{ marginTop: 10 }}>
            <thead>
              <tr>
                <th>Part</th>
                <th>Rev</th>
                <th>Faces</th>
                <th>Waypoints</th>
                <th>Collisions</th>
                <th>MOD</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {lastRun.parts.map((p) => (
                <tr key={p.step_filename}>
                  <td>{p.part_id}</td>
                  <td>{p.revision || '—'}</td>
                  <td>{p.selected_face_count}/{p.face_count}</td>
                  <td>{p.waypoint_count}</td>
                  <td>
                    {p.collision_count > 0
                      ? <Tag intent="danger" minimal>{p.collision_count}</Tag>
                      : <Tag intent="success" minimal>0</Tag>}
                  </td>
                  <td>{p.rapid_bytes > 0 ? `${p.rapid_bytes} B` : '—'}</td>
                  <td>
                    {p.ok ? <Icon icon="tick" intent="success" />
                          : <Tag intent="danger" minimal>fail</Tag>}
                  </td>
                </tr>
              ))}
            </tbody>
          </HTMLTable>
        )}
      </Card>

      {proj.outputs.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <h3>Outputs ({proj.outputs.length})</h3>
          <ul className="projects-outputs">
            {proj.outputs.map((o) => (
              <li key={o}>
                <a href={`/api/projects/${proj.project_id}/output/${o}`} target="_blank" rel="noreferrer">
                  {o}
                </a>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {proj.inputs.length === 0 && (
        <NonIdealState
          icon="document-open"
          title="No STEP files yet"
          description="Drop a .step/.stp (or many) into the Inputs card above."
        />
      )}
    </div>
  )
}

function DropZone({ onDrop, uploading }: { onDrop: (files: FileList | null) => void; uploading: boolean }) {
  const [hover, setHover] = useState(false)
  return (
    <div
      className={`dropzone ${hover ? 'dropzone-hover' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setHover(true) }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => { e.preventDefault(); setHover(false); onDrop(e.dataTransfer.files) }}
    >
      <Icon icon="cloud-upload" size={28} />
      <div>Drop STEP files here, or</div>
      <label className="bp5-button bp5-intent-primary bp5-small" style={{ cursor: 'pointer' }}>
        <input
          type="file"
          multiple
          accept=".step,.stp,.STEP,.STP"
          onChange={(e) => onDrop(e.target.files)}
          style={{ display: 'none' }}
        />
        {uploading ? 'Uploading…' : 'Browse'}
      </label>
    </div>
  )
}
